'use client';
import {
  type ElementType,
  type FC,
  type ReactNode,
  Children,
  useMemo,
} from 'react';

import { cva, cx } from '../helpers/cva/index.js';
import { createComponent } from '../helpers/create-component.js';
import { IconAcceptableValue, IconRenderer } from '../Icon/index.js';
import { forwardRefWithAs } from '../helpers/forward-ref-with-as.js';
import { Typography, type TypographyProps } from '../Typography/index.js';
import { useBrand } from '../../brand/context/index.js';
import type { HTMLComponentProps, OverrideProps } from '../../types/helpers.js';
import LoadingIcon from '../Icon/icons/LoadingIcon/index.js';
import { Brand } from '../../brand/index.js';

export type ButtonProps = OverrideProps<
  [
    HTMLComponentProps<'button'>,
    {
      /**
       * Which type of button should be rendered?
       * @default 'primary'
       */
      buttonType?: 'primary' | 'secondary' | 'text';
      /**
       * Render a text button with no padding?
       * @default false
       */
      flush?: boolean;
      /**
       * An icon to display at the start of the button.
       * Using an `IconName` string is deprecated.
       */
      icon?: IconAcceptableValue;
      /**
       * An icon to display at the end of the button.
       * Using an `IconName` string is deprecated.
       */
      iconEnd?: IconAcceptableValue;
      /**
       * An icon to display at the start the button. Same as `icon`.
       * Using an `IconName` string is deprecated.
       */
      iconStart?: IconAcceptableValue;
      /**
       * Display the loading state?
       * @default false
       */
      loading?: boolean;
      /**
       * How large should the button be?
       * @default 'md'
       */
      size?: 'sm' | 'md' | 'lg';
      /**
       * Which color should the button be?
       * @default 'brand'
       */
      state?: 'brand' | 'dark' | 'critical' | 'success';
    },
  ]
>;

const getButtonClasses = cva({
  base: [
    'cur-relative',
    'cur-inline-block',
    'cur-border-2',
    'cur-font-body',
    'cur-text-body',
    'cur-tracking-body',
    'cur-transition-all',
  ],
  variants: {
    brand: {
      curology: null,
      agency: null,
    },
    buttonType: {
      primary: [],
      secondary: [
        'cur-bg-surface-neutral',
        'active:cur-bg-surface-neutral-selected',
      ],
      text: 'cur-border-transparent cur-group active:cur-text-primary',
    },
    disabled: {
      true: null,
    },
    flush: {
      true: null,
    },
    flushAndText: {
      true: null,
    },
    iconOnly: {
      true: 'cur-rounded-full',
    },
    loading: {
      true: null,
    },
    size: {
      sm: null,
      md: null,
      lg: 'cur-leading-[2]',
    },
    state: {
      brand: null,
      dark: null,
      critical: null,
      success: null,
    },
  },
  compoundVariants: [
    // Small Buttons
    {
      size: 'sm',
      flush: false,
      flushAndText: false,
      className: 'cur-h-[2.5rem]',
    },
    {
      size: 'sm',
      flush: false,
      iconOnly: true,
      className: 'cur-w-[2.5rem]',
    },
    {
      size: 'sm',
      flushAndText: false,
      iconOnly: false,
      className: 'cur-py-2 cur-px-4',
    },

    // Small/Medium Buttons
    {
      size: ['sm', 'md'],
      className: 'cur-leading-[1.5]',
    },

    // Medium Buttons
    {
      size: 'md',
      flush: false,
      flushAndText: false,
      className: 'cur-h-[3.5rem]',
    },
    {
      size: 'md',
      flush: false,
      iconOnly: true,
      className: 'cur-w-[3.5rem]',
    },
    {
      size: ['md', 'lg'],
      flushAndText: false,
      iconOnly: false,
      className: 'cur-py-4 cur-px-6',
    },

    // Large Buttons
    {
      size: 'lg',
      flush: false,
      flushAndText: false,
      className: 'cur-h-[4rem]',
    },
    {
      size: 'lg',
      flush: false,
      iconOnly: true,
      className: 'cur-w-[4rem]',
    },

    // Text Buttons
    {
      buttonType: 'text',
      disabled: false,
      iconOnly: true,
      loading: false,
      className: 'hover:cur-bg-surface-highlight',
    },

    // Primary Buttons
    {
      buttonType: 'primary',
      disabled: false,
      className: 'cur-text-inverse',
    },
    {
      buttonType: 'primary',
      disabled: true,
      loading: false,
      className: 'cur-border-surface-disabled',
    },
    {
      buttonType: 'primary',
      state: 'brand',
      disabled: false,
      className: [
        'cur-bg-surface-brand',
        'cur-border-surface-brand',
        'hover:cur-bg-surface-brand-hover',
        'hover:cur-border-surface-brand-hover',
        'active:cur-border-surface-brand-selected',
        'active:cur-bg-surface-brand-selected',
      ],
    },
    {
      buttonType: 'primary',
      state: 'dark',
      disabled: false,
      className: [
        'cur-bg-surface-secondary',
        'hover:cur-bg-surface-secondary-hover',
        'active:cur-bg-surface-secondary-selected',
        'hover:cur-border-surface-secondary-hover',
        'active:cur-border-surface-secondary-selected',
      ],
    },
    {
      buttonType: 'primary',
      state: 'critical',
      disabled: false,
      className: [
        'cur-bg-surface-error-bold',
        'hover:cur-bg-surface-error-bold-hover',
        'active:cur-bg-surface-error-bold-selected',
        'hover:cur-border-surface-error-bold-hover',
        'active:cur-border-surface-error-bold-selected',
      ],
    },
    {
      buttonType: 'primary',
      state: 'success',
      disabled: false,
      className: [
        'cur-border-surface-success-bold',
        'cur-bg-surface-success-bold',
        'hover:cur-bg-surface-success-bold-hover',
        'active:cur-bg-surface-success-bold-selected',
        'hover:cur-border-surface-success-bold-hover',
        'active:cur-border-surface-success-bold-selected',
      ],
    },

    // Primary/Secondary Buttons
    {
      buttonType: ['primary', 'secondary'],
      className: 'cur-rounded-[3.5rem]',
    },
    {
      buttonType: ['primary', 'secondary'],
      disabled: true,
      loading: false,
      className: 'cur-bg-surface-disabled',
    },
    {
      buttonType: ['primary', 'secondary'],
      state: 'dark',
      disabled: false,
      className: 'cur-border-border-secondary',
    },
    {
      buttonType: ['primary', 'secondary'],
      state: 'critical',
      disabled: false,
      className: 'cur-border-border-error',
    },

    // Secondary Buttons
    {
      buttonType: 'secondary',
      disabled: true,
      loading: false,
      className: 'cur-border-border-disabled',
    },
    {
      buttonType: 'secondary',
      state: 'brand',
      disabled: false,
      className: 'cur-border-surface-brand-light',
    },
    {
      buttonType: 'secondary',
      state: ['brand', 'dark'],
      disabled: false,
      className: 'hover:cur-bg-surface-neutral-hover',
    },
    {
      buttonType: 'secondary',
      state: 'critical',
      disabled: false,
      className: [
        'hover:cur-bg-surface-error-hover',
        'active:cur-bg-surface-error-selected',
      ],
    },
    {
      buttonType: 'secondary',
      state: 'success',
      disabled: false,
      className: [
        'cur-border-border-success',
        'hover:cur-bg-surface-success-hover',
        'active:cur-bg-surface-success-selected',
      ],
    },

    // Secondary or Text
    {
      buttonType: ['secondary', 'text'],
      state: 'brand',
      disabled: false,
      className: 'cur-text-brand',
    },
    {
      buttonType: ['secondary', 'text'],
      state: 'dark',
      disabled: false,
      className: 'cur-text-primary',
    },
    {
      buttonType: ['secondary', 'text'],
      state: 'critical',
      disabled: false,
      className: 'cur-text-error',
    },
    {
      buttonType: ['secondary', 'text'],
      state: 'success',
      disabled: false,
      className: 'cur-text-success',
    },

    // Disabled but not loading
    {
      disabled: true,
      loading: false,
      className: 'cur-text-disabled',
    },

    // AGENCY
    {
      brand: 'agency',
      buttonType: ['primary', 'secondary'],
      className: 'cur-rounded-none',
    },
    {
      brand: 'agency',
      buttonType: ['primary', 'secondary', 'text'],
      className: 'cur-uppercase cur-font-normal cur-tracking-wider',
    },
    {
      brand: 'agency',
      state: 'brand',
      buttonType: ['primary', 'text'],
      className: 'cur-text-primary',
    },
    {
      brand: 'agency',
      buttonType: ['secondary'],
      className:
        'cur-border-surface-secondary cur-text-surface-secondary active:cur-bg-surface-secondary-selected active:cur-text-surface-primary hover:cur-bg-surface-secondary hover:cur-text-surface-primary cur-border',
    },
    {
      brand: 'agency',
      disabled: true,
      loading: false,
      className: 'cur-border-surface-disabled cur-text-disabled',
    },
  ],
});

export type ButtonMetaProps = TypographyProps;
export const ButtonMeta: FC<TypographyProps> = ({ children, ...props }) => (
  <Typography size="meta" className="cur-inline-block" {...props}>
    {children}
  </Typography>
);

const ButtonCTA: FC<{
  children: ReactNode;
  disabled: boolean;
  brand?: Brand;
}> = ({ children, disabled, brand }) => (
  <div
    className={cx('cur-font-bold', {
      'group-hover:cur-underline': !disabled,
      'cur-font-normal': brand && brand === 'agency',
    })}
  >
    {children}
  </div>
);

const useIconSize = (size: ButtonProps['size'], iconOnly: boolean) =>
  useMemo(() => {
    if (iconOnly && size === 'lg') {
      return 'md';
    }
    return 'sm';
  }, [size, iconOnly]);

export const Button = createComponent(
  'Typography',
  forwardRefWithAs<'button', ButtonProps>(
    (
      {
        as,
        buttonType = 'primary',
        children,
        className = '',
        disabled = false,
        flush = false,
        icon,
        iconEnd,
        iconStart,
        loading = false,
        onClick,
        size = 'md',
        state = 'brand',
        type = 'button',
        ...props
      },
      ref,
    ) => {
      const brand = useBrand();
      iconStart = iconStart || icon;
      const Component = (as || 'button') as ElementType;
      const iconOnly = !children && !!(iconStart || iconEnd);
      const buttonClasses = getButtonClasses({
        brand,
        buttonType,
        className,
        disabled,
        flush,
        flushAndText: flush && buttonType === 'text',
        iconOnly,
        loading,
        size,
        state,
      });
      const isDisabled = disabled || loading;

      const iconSize = useIconSize(size, iconOnly);

      return (
        <Component
          type={type}
          ref={ref}
          className={buttonClasses}
          disabled={isDisabled}
          {...props}
          onClick={onClick}
        >
          <span
            className={cx(
              'cur-flex cur-h-full cur-items-center cur-justify-center cur-text-ellipsis cur-whitespace-nowrap',
              { 'cur-invisible': loading },
            )}
          >
            <IconRenderer
              icon={iconStart}
              size={iconSize}
              className={children ? 'cur-mr-2' : ''}
            />
            <span className="cur-flex cur-h-full cur-items-center cur-gap-1">
              {Children.map(children, (child) => {
                // Only wrap non-component children in a CTA
                if (typeof child === 'string') {
                  return (
                    <ButtonCTA disabled={isDisabled} brand={brand}>
                      {child}
                    </ButtonCTA>
                  );
                }
                return child;
              })}
            </span>
            <IconRenderer
              icon={iconEnd}
              size={iconSize}
              className={children ? 'cur-ml-2' : ''}
            />
          </span>
          {loading && (
            <span className="cur-absolute cur-left-1/2 cur-top-1/2 -cur-translate-x-1/2 -cur-translate-y-1/2">
              <LoadingIcon size={iconSize} className="cur-animate-spin" />
            </span>
          )}
        </Component>
      );
    },
  ),
);
